/**
 * The address of somebody's website, as they read it and as they reach it.
 *
 * The owner built a website and could not find it: on the phone its address was only inside an
 * alert behind ⋯ → Your website, the build's own message said "Share its link" without the link,
 * and the one fast route was an unlabelled icon on the Build list. These two helpers are what
 * every one of those places now shows and opens, so they all say the same thing.
 *
 * No imports, deliberately: the web builder reads the same words, and a helper that pulled in
 * the phone's theme or stores could not be shared with it.
 */

/**
 * The address without its scheme, e.g. `app.saddle-sites.com/site/shot-tracker-golf-2`.
 *
 * What a person reads aloud, types into a browser, or recognises as theirs. `https://` says
 * nothing to them and costs a third of the width a phone header has. A query or fragment is not
 * part of the address anybody hands out, and a trailing slash is noise. Opening it always uses
 * the full URL; this is only ever what is shown.
 */
export function displayAddress(url: string | null | undefined): string {
  if (!url) return '';
  return url
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '');
}

/** The fields of a project record that say whether it has a website to go to. */
export interface SiteRecord {
  runtime?: string;
  webUrl?: string;
  unfinished?: unknown;
}

/**
 * The website a project record points at — or null when there is none to show.
 *
 * Only a web app has one, and only once it has an address. An unfinished build has nothing to
 * open even when an older address is still on its record (a rebuild that failed keeps the one
 * before it): showing that address beside "Not finished yet" would send the owner to a page
 * that is not the app they are waiting for.
 *
 * Whether the page is actually being SERVED is not decided here and cannot be: that is the
 * server's answer (see `siteStatusOf`), and a local record is never the source of truth for
 * whether a page is public. This only says where the page lives.
 */
export function siteLink(record: SiteRecord | null | undefined): { url: string; address: string } | null {
  if (!record || record.runtime !== 'webapp' || record.unfinished || !record.webUrl) return null;
  const address = displayAddress(record.webUrl);
  return address ? { url: record.webUrl, address } : null;
}
